import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Upload, Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { GoldButton } from "@/components/Brand";
import { FocusButton } from "@/components/admin/FocusDialog";

const field = "min-h-[42px] w-full border border-[#C69A3B]/60 bg-white px-2 text-sm";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const FRAME_STYLES = [["black", "lab.frame.black"], ["wood", "lab.frame.wood"], ["gold", "lab.frame.gold"]];

const SERVICE_SLOTS = [
  ["event", "Fotografía de Eventos"],
  ["reunion", "Fotografía de Reuniones"],
  ["graduation", "Fotografía de Graduación"],
  ["portrait", "Sesiones de Retrato"],
  ["family", "Eventos Familiares"],
  ["digital", "Galerías Digitales"],
  ["restoration", "Restauración de Fotos"],
  ["custom", "Paquetes Personalizados"],
];

const uploadImage = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/admin/site-images", form);
  return data.url;
};

const ImageStrip = ({ images, onRemove, testid, aspect = "aspect-[4/3]" }) => (
  <div className="mt-4 flex flex-wrap gap-3" data-testid={testid}>
    {images.length === 0 && <p className="text-xs text-[#5A5752]">—</p>}
    {images.map((src, i) => (
      <div key={src + i} className="w-24">
        <div className="relative h-24 w-24 overflow-hidden border border-[#C69A3B]/40">
          <img src={src} alt="" className="h-full w-full object-cover" />
          <button
            type="button"
            data-testid={`${testid}-remove-${i}`}
            onClick={() => onRemove(i)}
            className="absolute right-0 top-0 grid h-7 w-7 place-items-center bg-[#8F3329] text-[#FAFAF7]"
            aria-label="remove"
          >
            <Trash2 size={12} />
          </button>
        </div>
        <div className="mt-1">
          <FocusButton url={src} aspect={aspect} testId={`${testid}-focus-${i}`} />
        </div>
      </div>
    ))}
  </div>
);

const UploadButton = ({ onFiles, busy, testid, label }) => {
  const ref = useRef(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => {
          onFiles(Array.from(e.target.files || []));
          e.target.value = "";
        }}
      />
      <button
        type="button"
        disabled={busy}
        data-testid={testid}
        onClick={() => ref.current?.click()}
        className="inline-flex min-h-[42px] items-center gap-2 border border-[#C69A3B] px-4 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7] disabled:opacity-40"
      >
        <Upload size={13} /> {label}
      </button>
    </>
  );
};

export default function AdminSiteImages() {
  const { t } = useI18n();
  const [home, setHome] = useState({ portrait_images: [], featured_images: [] });
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState({ category_es: "", category_en: "" });
  const [busy, setBusy] = useState(false);
  const [frameSample, setFrameSample] = useState({});
  const [sampleVersion, setSampleVersion] = useState(0);

  useEffect(() => {
    api.get("/admin/frame-sample").then((r) => setFrameSample(r.data.images || {})).catch(() => {});
  }, []);

  const saveFrameSample = async (images) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/frame-sample", { images });
      setFrameSample(data.images || {});
      setSampleVersion((v) => v + 1);
      toast.success(t("admin.siteSaved"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setFrameSampleImage = async (style, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadImage(file);
      await saveFrameSample({ ...frameSample, [style]: url });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  const load = useCallback(() => {
    api.get("/site/home").then((r) => setHome(r.data)).catch(() => {});
    api.get("/admin/portfolio").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const saveHome = async (updates) => {
    const { data } = await api.patch("/admin/site/home", updates);
    setHome(data);
    toast.success(t("admin.siteSaved"));
  };

  const addHomeImages = async (key, files) => {
    setBusy(true);
    try {
      const urls = [];
      for (const file of files) urls.push(await uploadImage(file));
      await saveHome({ [key]: [...(home[key] || []), ...urls] });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setServiceImage = async (key, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadImage(file);
      await saveHome({ service_images: { ...(home.service_images || {}), [key]: url } });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveCategory = async (cat, updates) => {
    try {
      const { data } = await api.patch(`/admin/portfolio/${cat.id}`, updates);
      setCategories((list) => list.map((c) => (c.id === cat.id ? data : c)));
      toast.success(t("admin.siteSaved"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const addCategoryImages = async (cat, files) => {
    setBusy(true);
    try {
      const urls = [];
      for (const file of files) urls.push(await uploadImage(file));
      await saveCategory(cat, { images: [...(cat.images || []), ...urls] });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const createCategory = async () => {
    if (!newCat.category_es.trim()) return;
    try {
      const { data } = await api.post("/admin/portfolio", newCat);
      setCategories((list) => [...list, data]);
      setNewCat({ category_es: "", category_en: "" });
      toast.success(t("admin.siteSaved"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`${cat.category_es}\n\n${t("admin.categoryDeleteConfirm")}`)) return;
    try {
      await api.delete(`/admin/portfolio/${cat.id}`);
      setCategories((list) => list.filter((c) => c.id !== cat.id));
      toast.success(t("admin.siteSaved"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-site-images-panel">
      <p className="text-sm text-[#5A5752]">{t("admin.siteImagesHelp")}</p>

      <section className="border border-[#C69A3B]/40 bg-white p-6" data-testid="home-portraits-section">
        <p className="label-mono">{t("admin.homePortraits")}</p>
        <p className="mt-2 text-xs text-[#5A5752]">{t("admin.homePortraitsHelp")}</p>
        <ImageStrip
          images={home.portrait_images || []}
          testid="home-portraits"
          aspect="aspect-[3/4]"
          onRemove={(i) =>
            saveHome({ portrait_images: home.portrait_images.filter((_, idx) => idx !== i) })
          }
        />
        <div className="mt-4">
          <UploadButton
            testid="home-portraits-upload"
            busy={busy}
            label={t("admin.addImages")}
            onFiles={(files) => addHomeImages("portrait_images", files)}
          />
        </div>
      </section>

      <section className="border border-[#C69A3B]/40 bg-white p-6" data-testid="home-featured-section">
        <p className="label-mono">{t("admin.homeFeatured")}</p>
        <p className="mt-2 text-xs text-[#5A5752]">{t("admin.homeFeaturedHelp")}</p>
        <ImageStrip
          images={home.featured_images || []}
          testid="home-featured"
          onRemove={(i) =>
            saveHome({ featured_images: home.featured_images.filter((_, idx) => idx !== i) })
          }
        />
        <div className="mt-4">
          <UploadButton
            testid="home-featured-upload"
            busy={busy}
            label={t("admin.addImages")}
            onFiles={(files) => addHomeImages("featured_images", files)}
          />
        </div>
      </section>

      <section className="border border-[#C69A3B]/40 bg-white p-6" data-testid="frame-sample-section">
        <p className="label-mono">{t("admin.frameSample")}</p>
        <p className="mt-2 text-xs text-[#5A5752]">{t("admin.frameSampleHelp")}</p>
        <div className="mt-5 grid gap-6 sm:grid-cols-3" data-testid="frame-sample-slots">
          {FRAME_STYLES.map(([style, label]) => (
            <div key={style} data-testid={`frame-sample-slot-${style}`}>
              <p className="text-sm font-medium">{t(label)}</p>
              <img
                src={`${API_BASE}/api/frame-samples/${style}?v=${sampleVersion}`}
                alt={t(label)}
                data-testid={`frame-sample-${style}`}
                className="mt-2 w-full border border-[#C69A3B]/20"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <UploadButton
                  testid={`frame-sample-upload-${style}`}
                  busy={busy}
                  label={t("admin.frameSamplePick")}
                  onFiles={(files) => setFrameSampleImage(style, files[0])}
                />
                {frameSample[style] && (
                  <button
                    type="button"
                    data-testid={`frame-sample-clear-${style}`}
                    onClick={() => saveFrameSample({ ...frameSample, [style]: "" })}
                    className="min-h-[40px] border border-[#8F3329] px-4 text-xs text-[#8F3329]"
                  >
                    {t("admin.frameSampleReset")}
                  </button>
                )}
              </div>
              {!frameSample[style] && (
                <p className="mt-2 text-[11px] text-[#5A5752]">{t("admin.frameSampleAuto")}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="border border-[#C69A3B]/40 bg-white p-6" data-testid="services-images-section">
        <p className="label-mono">{t("admin.serviceImages")}</p>
        <p className="mt-2 text-xs text-[#5A5752]">{t("admin.serviceImagesHelp")}</p>
        <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICE_SLOTS.map(([key, label]) => {
            const current = (home.service_images || {})[key];
            return (
              <div key={key} data-testid={`service-slot-${key}`}>
                <p className="text-sm font-medium">{label}</p>
                <div className="mt-2 h-24 w-full overflow-hidden border border-[#C69A3B]/40 bg-[#EFEAE0]">
                  {current && <img src={current} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <UploadButton
                    testid={`service-upload-${key}`}
                    busy={busy}
                    label={t("admin.addImages")}
                    onFiles={(files) => setServiceImage(key, files[0])}
                  />
                  {current && (
                    <FocusButton url={current} aspect="aspect-[3/2]" testId={`service-focus-${key}`} />
                  )}
                  {current && (
                    <button
                      type="button"
                      data-testid={`service-clear-${key}`}
                      onClick={() => {
                        const next = { ...(home.service_images || {}) };
                        delete next[key];
                        saveHome({ service_images: next });
                      }}
                      className="min-h-[42px] border border-[#8F3329] px-3 text-xs text-[#8F3329]"
                    >
                      {t("btn.delete")}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="border border-[#C69A3B]/40 bg-white p-6" data-testid="portfolio-section">
        <p className="label-mono">{t("admin.portfolioCategories")}</p>
        <div className="mt-5 space-y-8">
          {categories.map((cat) => (
            <div key={cat.id} className="border-t border-[#D8D3CB] pt-5" data-testid={`portfolio-cat-${cat.slug}`}>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  className={`${field} max-w-[220px]`}
                  data-testid={`portfolio-cat-es-${cat.slug}`}
                  defaultValue={cat.category_es || ""}
                  onBlur={(e) =>
                    e.target.value.trim() &&
                    e.target.value !== cat.category_es &&
                    saveCategory(cat, { category_es: e.target.value.trim() })
                  }
                />
                <input
                  className={`${field} max-w-[220px]`}
                  data-testid={`portfolio-cat-en-${cat.slug}`}
                  defaultValue={cat.category_en || ""}
                  onBlur={(e) =>
                    e.target.value.trim() &&
                    e.target.value !== cat.category_en &&
                    saveCategory(cat, { category_en: e.target.value.trim() })
                  }
                />
                <span className="text-xs text-[#5A5752]">
                  {(cat.images || []).length} {t("events.photos")}
                </span>
                <UploadButton
                  testid={`portfolio-upload-${cat.slug}`}
                  busy={busy}
                  label={t("admin.addImages")}
                  onFiles={(files) => addCategoryImages(cat, files)}
                />
                <button
                  type="button"
                  data-testid={`portfolio-cat-delete-${cat.slug}`}
                  onClick={() => deleteCategory(cat)}
                  className="inline-flex min-h-[42px] items-center gap-2 border border-[#8F3329] px-4 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
                >
                  <Trash2 size={13} /> {t("btn.delete")}
                </button>
              </div>
              <ImageStrip
                images={cat.images || []}
                testid={`portfolio-images-${cat.slug}`}
                onRemove={(i) =>
                  saveCategory(cat, { images: cat.images.filter((_, idx) => idx !== i) })
                }
              />
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-[#D8D3CB] pt-5">
          <input
            className={`${field} max-w-[220px]`}
            placeholder="Categoría — Español"
            data-testid="new-category-es"
            value={newCat.category_es}
            onChange={(e) => setNewCat((c) => ({ ...c, category_es: e.target.value }))}
          />
          <input
            className={`${field} max-w-[220px]`}
            placeholder="Category — English"
            data-testid="new-category-en"
            value={newCat.category_en}
            onChange={(e) => setNewCat((c) => ({ ...c, category_en: e.target.value }))}
          />
          <GoldButton onClick={createCategory} data-testid="new-category-btn">
            <Plus size={14} /> {t("admin.addCategory")}
          </GoldButton>
        </div>
      </section>
    </div>
  );
}
